import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Lock, BadgeCheck, FileLock2, ArrowLeft, Server, Eye, Trash2, Clock } from "lucide-react";

const SECTIONS = [
  {
    icon: Server, title: "What we collect and why",
    body: "To open a regulated investment account we must verify who you are. We collect your name, date of birth, address, citizenship, tax residency, employment and source of wealth, plus a government ID and a live selfie. We use it only to verify your identity, meet anti-money-laundering obligations and operate your account.",
  },
  {
    icon: Lock, title: "How your data is protected",
    body: "Everything you submit travels over TLS 1.2+ with 256-bit encryption and is stored encrypted at rest. Access is limited to authorised compliance staff on a least-privilege basis, and every access is logged in an immutable audit trail. Sessions expire automatically after 30 minutes of inactivity.",
  },
  {
    icon: BadgeCheck, title: "Who we share it with",
    body: "Your identity documents are shared only with our regulated verification partner and, where legally required, with regulators, tax authorities and our execution brokers. We never sell your data and we never share it with advertisers.",
  },
  {
    icon: Clock, title: "How long we keep it",
    body: "Regulation requires us to retain identification records for a minimum period after your account closes — typically five years, or longer where a specific jurisdiction demands it. After that, records are securely destroyed.",
  },
  {
    icon: Eye, title: "Your rights",
    body: "You can request a copy of the personal data we hold, ask us to correct it, or object to specific processing. Write to privacy@tharwy.com and we will respond within 30 days.",
  },
  {
    icon: Trash2, title: "Deleting your data",
    body: "You may close your account at any time. We will delete everything we are not legally obliged to retain, and tell you exactly what must be kept and for how long.",
  },
];

export default function Privacy() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 h-16 flex items-center gap-3">
          <Button variant="ghost" size="sm" className="gap-1.5 rounded-full" onClick={() => navigate(-1)}
            data-testid="privacy-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <span className="ms-auto font-heading font-bold">Tharwy</span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 sm:px-8 py-12" data-testid="privacy-page">
        <span className="text-xs font-bold uppercase tracking-[0.18em] text-[color:var(--tharwy-lime-700)]">Legal</span>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-3">Privacy &amp; data protection</h1>
        <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
          How Tharwy handles the information you give us during account opening and afterwards.
          Last updated June 2026.
        </p>

        <div className="flex flex-wrap gap-x-5 gap-y-2 mt-7 text-xs">
          {[[Lock, "256-bit bank-grade encryption"], [BadgeCheck, "Regulated conduct — QFC / DFSA aligned"],
            [FileLock2, "ISO 27001 information security"]].map(([Icon, label]) => (
            <span key={label} className="flex items-center gap-1.5 text-muted-foreground">
              <Icon className="h-3.5 w-3.5 text-[color:var(--tharwy-lime-700)]" />{label}
            </span>
          ))}
        </div>

        <div className="mt-12 space-y-9">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="font-heading text-lg font-bold flex items-center gap-2.5">
                <span className="h-9 w-9 rounded-xl bg-accent flex items-center justify-center shrink-0">
                  <s.icon className="h-4 w-4 text-[hsl(var(--accent-foreground))]" />
                </span>
                {s.title}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed mt-3">{s.body}</p>
            </section>
          ))}
        </div>

        <p className="mt-14 text-xs text-muted-foreground leading-relaxed border-t border-border pt-6">
          Tharwy operates as a demonstration platform. Regulatory references are shown to illustrate the
          standards the production service is being built to meet.
        </p>
      </main>
    </div>
  );
}
